import { getManufacturerFamilyIds, SerializedManufacturer, SerializedMeterModel } from '@enmon/commons/lib/tools';

export const filterModelsByManufacturerFamily = (
  models: SerializedMeterModel[],
  manufacturerId: SerializedManufacturer['id'] | undefined,
): SerializedMeterModel[] => {
  if (!manufacturerId) {
    return [];
  }

  const manufacturers = [
    ...new Map(models.flatMap(({ manufacturer }) => (manufacturer ? [[manufacturer.id, manufacturer]] : []))).values(),
  ];
  const familyIds = getManufacturerFamilyIds(manufacturers, manufacturerId);

  return models.filter(model => model.manufacturer && familyIds.has(model.manufacturer.id));
};

export type ManufacturerModelGroup = {
  manufacturer: Pick<SerializedManufacturer, 'id' | 'name'>;
  isPicked: boolean;
  models: SerializedMeterModel[];
};

/**
 * The family's models grouped by the manufacturer that owns them: the picked manufacturer first,
 * the rest of its family alphabetically.
 */
export const groupModelsByManufacturerFamily = (
  models: SerializedMeterModel[],
  manufacturerId: SerializedManufacturer['id'] | undefined,
): ManufacturerModelGroup[] => {
  const groups = new Map<SerializedManufacturer['id'], ManufacturerModelGroup>();

  for (const model of filterModelsByManufacturerFamily(models, manufacturerId)) {
    const { id, name } = model.manufacturer as SerializedManufacturer; // the filter keeps only models with one
    const group = groups.get(id) ?? { manufacturer: { id, name }, isPicked: id === manufacturerId, models: [] };
    group.models.push(model);
    groups.set(id, group);
  }

  return [...groups.values()].sort(
    (a, b) => Number(b.isPicked) - Number(a.isPicked) || a.manufacturer.name.localeCompare(b.manufacturer.name),
  );
};
